import { useState, useCallback } from "react";
import { Button, Modal, useToast } from "@/components/ds";
import { ValidationError } from "./errors";
import type { ClashInfo } from "@/domain/shifts/types";
import styles from "./FillWeek.module.css";

interface Cleaner {
  readonly membershipId: string;
  readonly name: string;
}

interface FillWeekProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly siteName: string;
  readonly siteId: string;
  readonly weekDates: readonly string[];
  readonly cleaners: readonly Cleaner[];
  readonly onCreateShift: (data: {
    siteId: string;
    membershipId: string;
    date: string;
    startTime: string;
    endTime: string;
  }) => Promise<{ clashes?: readonly ClashInfo[] }>;
}

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

export function FillWeek({
  open,
  onClose,
  siteName,
  siteId,
  weekDates,
  cleaners,
  onCreateShift,
}: FillWeekProps) {
  const { toast } = useToast();
  const [selectedDays, setSelectedDays] = useState<boolean[]>([false, false, false, false, false, false, false]);
  const [membershipId, setMembershipId] = useState(cleaners[0]?.membershipId ?? "");
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("11:00");
  const [saving, setSaving] = useState(false);

  const toggleDay = useCallback((index: number) => {
    setSelectedDays((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  }, []);

  const selectedCount = selectedDays.filter(Boolean).length;

  const handleFill = useCallback(async () => {
    if (saving || selectedCount === 0 || !membershipId) return;
    setSaving(true);

    try {
      let created = 0;
      for (let i = 0; i < 7; i++) {
        if (!selectedDays[i]) continue;
        try {
          await onCreateShift({
            siteId,
            membershipId,
            date: weekDates[i],
            startTime,
            endTime,
          });
          created++;
        } catch (error) {
          if (error instanceof ValidationError) {
            toast("error", `Failed for ${DAY_LABELS[i]}: ${Object.values(error.fields).join(", ")}`);
          }
        }
      }
      if (created > 0) {
        toast("success", `Added ${created} ${created === 1 ? "shift" : "shifts"}`);
      }
      onClose();
    } finally {
      setSaving(false);
    }
  }, [saving, selectedCount, membershipId, selectedDays, weekDates, siteId, startTime, endTime, onCreateShift, onClose, toast]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Fill week: ${siteName}`}
      size="sm"
      footer={
        <div style={{ display: "flex", gap: "var(--space-2)", justifyContent: "flex-end" }}>
          <Button size="sm" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleFill} disabled={saving || selectedCount === 0 || !membershipId}>
            {saving ? "Adding..." : selectedCount === 0 ? "Select days" : `Add ${selectedCount} ${selectedCount === 1 ? "shift" : "shifts"}`}
          </Button>
        </div>
      }
    >
      <p className={styles.hint}>Select the days, who's cleaning, and what time.</p>

      <div className={styles.dayGrid}>
        {DAY_LABELS.map((label, i) => (
          <button
            key={label}
            type="button"
            className={`${styles.dayToggle} ${selectedDays[i] ? styles.dayToggleActive : ""}`}
            onClick={() => toggleDay(i)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className={styles.fieldFull}>
        <label className={styles.label} htmlFor="fill-cleaner">Cleaner</label>
        <select
          id="fill-cleaner"
          style={{ width: "100%", height: "36px", padding: "0 8px", borderRadius: "var(--radius-sm)", border: "1px solid var(--color-border-default)", fontSize: "var(--type-small-size)" }}
          value={membershipId}
          onChange={(e) => setMembershipId(e.target.value)}
        >
          {cleaners.length === 0 && <option value="">No cleaners available</option>}
          {cleaners.map((c) => (
            <option key={c.membershipId} value={c.membershipId}>{c.name}</option>
          ))}
        </select>
      </div>

      <div className={styles.fieldRow}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="fill-start">Start</label>
          <input
            type="time"
            id="fill-start"
            style={{ height: "36px", padding: "0 8px", borderRadius: "var(--radius-sm)", border: "1px solid var(--color-border-default)", fontSize: "var(--type-small-size)" }}
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="fill-end">End</label>
          <input
            type="time"
            id="fill-end"
            style={{ height: "36px", padding: "0 8px", borderRadius: "var(--radius-sm)", border: "1px solid var(--color-border-default)", fontSize: "var(--type-small-size)" }}
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
